import {
	ChevronDown,
	Download as DownloadIcon,
} from '@audio-network/componentlibrary/icons'
import React, { useState } from 'react'

import { DownloadProps } from './Download.types'
import FlyoutMenu from '../FlyoutMenu'
import { ProgressBar } from './components/ProgressBar'
import Spinner from './components/Spinner/Spinner'
import clsx from 'clsx'
import styles from './Download.module.css'
import { useDownload } from './Download.hooks'
import { useSession } from '../../../context/Session'

// TODO: make downloadText, downloadOptions, downloadOptionWav, downloadOptionMp3 optional and only grab translations if they do not exist
const Download: React.FC<DownloadProps> = ({
	className,
	stemsInfo,
	tracks,
	trackSource,
	downloadName,
	downloadText,
	downloadOptions,
	downloadOptionWav,
	downloadOptionMp3,
	...props
}) => {
	const { deviceType, user } = useSession()

	const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)

	const { handleDownload, isDownloading, isFetching, progressBarRef } =
		useDownload({ downloadName, stemsInfo, tracks, trackSource })

	const id = `download-${
		downloadName ??
		stemsInfo?.track?.title ??
		// If tracks is an array, use first track's title, otherwise use the single track's title
		(Array.isArray(tracks) ? tracks[0]?.title : tracks?.title)
	}`
		.replace(/[\s\p{P}]/gu, '-')
		.toLowerCase()

	// TODO: Convert to popover menu
	const toggleFlyoutMenu = (): void => {
		setIsFlyoutOpen(!isFlyoutOpen)
	}

	// TODO: the majority of download buttons have downloadOptions apart from Stems and maybe SoundEffects
	// this can just be done anyway why a boolean?
	const flyoutMenuOptions = downloadOptions && [
		{
			disabled: isDownloading,
			title: `${downloadOptionWav}`,
			onClick: () => handleDownload('wav'),
		},
		{
			disabled: isDownloading,
			title: `${downloadOptionMp3}`,
			onClick: () => handleDownload('mp3'),
		},
	]

	// TODO: extend to consider sound-effects access - if download type is sound-effects && user can download sound-effects
	if (!user?.canDownload) {
		return null
	}

	// TODO: kill device type check for mobile put in return with display nones
	// the majority of download buttons have downloadOptions apart from Stems and maybe SoundEffects
	if (deviceType === 'mobile' && downloadOptions) {
		return (
			<>
				<button
					aria-label={downloadOptions}
					aria-controls={id}
					aria-expanded={isFlyoutOpen}
					disabled={isDownloading}
					onClick={toggleFlyoutMenu}
				>
					<DownloadIcon />
				</button>
				<FlyoutMenu
					id={id}
					isOpen={isFlyoutOpen}
					onClose={toggleFlyoutMenu}
					options={flyoutMenuOptions}
				/>
			</>
		)
	}

	// TODO: kill device type check for mobile put in return with display nones
	if (deviceType === 'mobile') {
		return (
			<button
				aria-label={downloadText}
				disabled={isDownloading}
				onClick={() => handleDownload()}
			>
				<DownloadIcon />
			</button>
		)
	}

	return (
		<div className={clsx(styles.downloadWrapper, className)} {...props}>
			<button
				className={styles.downloadButton}
				data-testid="download-button"
				onClick={() => handleDownload()}
				disabled={isDownloading}
			>
				<DownloadIcon
					name="Download"
					className={clsx(
						styles.icon,
						isFetching && styles.hide,
						!isFetching && isDownloading && styles.centered
					)}
				/>

				<span className={clsx(styles.text, isDownloading && styles.hide)}>
					{downloadText}
				</span>

				{/* TODO: think about how to handle spinner for mobile */}
				<Spinner aria-hidden={!isFetching} />
			</button>
			{downloadOptions && (
				<>
					<button
						aria-controls={id}
						aria-expanded={isFlyoutOpen}
						aria-label={downloadOptions}
						className={styles.options}
						onClick={toggleFlyoutMenu}
						disabled={isDownloading}
					>
						<ChevronDown color="textInverse" />
					</button>
					<FlyoutMenu
						id={id}
						isOpen={isFlyoutOpen}
						onClose={toggleFlyoutMenu}
						options={flyoutMenuOptions}
					/>
				</>
			)}
			{/* TODO: think about how to handle progress bar for mobile */}
			{!isFetching && isDownloading && <ProgressBar ref={progressBarRef} />}
		</div>
	)
}

export default React.memo(Download)
